import supportsWebRTC from '../utils/supportsWebRTC';
import logger from '../../log';
import { DeliveryMode } from '../constants';
import handleEcdn10DetectionData from './handleEcdn10DetectionData';
import hasAgentDataAnd10Agent from './hasAgentDataAnd10Agent';
import check10DataOrRetry from './check10DataOrRetry';
import ContentConfig from '../../config/ContentConfig';
import { PrimingData } from '../../priming/interfaces';
import get11Data from '../../priming/get11Data';
import deliveryCapable from '../../ecdn11/deliveryCapable';
import AgentData from '../../locality/AgentData';
import { DetectionType, PrimingError } from '../../reporter/constants';
import { ElevenAgentData } from '../../ecdn11/interfaces';
import { dispatchPrimingEvent } from '../../events/dispatch';

const log = logger.getLogger(__filename);

export interface Result {
  mode: DeliveryMode;
  vodOptOut?: boolean;
  data?: AgentData;
  elevenData?: ElevenAgentData;
}

const { NO_PLAYBACK } = DetectionType;
const { DISABLED, MISSING_DATA, NOT_CAPABLE, NO_PLAYBACK_METHOD, UNKNOWN } =
  PrimingError;
const { ECDN10, ECDN11, WEBRTC } = DeliveryMode;

const ecdn10callback = (
  data: AgentData,
  mode: DeliveryMode,
  config: ContentConfig
) => {
  // types ...
  const agentVOO = data.vod_optout;
  const vodOptOut =
    // We check for true or false here with loose == because we want to validate the boolean is set.
    agentVOO == true || agentVOO == false ? agentVOO : config.legacy.vodOptOut;
  const useEcdn10 = { mode, data };
  // Include vodOptOut only if it is true
  return vodOptOut ? { ...useEcdn10, vodOptOut } : useEcdn10;
};

export const handleEcdn10 = async (
  data: PrimingData,
  config: ContentConfig,
  mode: DeliveryMode
): Promise<Result> => {
  const type = DetectionType.ECDN10;
  try {
    if (is10Disabled(config)) {
      return Promise.reject({ message: DISABLED });
    }
    const ecdn10 = await check10DataOrRetry(data, config);
    const agentData = ecdn10 && ecdn10.agentData;
    if (!agentData || !hasAgentDataAnd10Agent(ecdn10)) {
      return Promise.reject({ message: MISSING_DATA });
    }
    dispatchPrimingEvent({ type, success: true });
    return Promise.resolve(ecdn10callback(agentData, mode, config));
  } catch (e) {
    const error = e as { message: PrimingError };
    const reason = (error && error.message) || UNKNOWN;
    dispatchPrimingEvent({ reason, type, success: false });
    return Promise.reject(error);
  }
};

export const is10Disabled = (config: ContentConfig): boolean => {
  return !!(config && config.legacy && config.legacy.disabled);
};

function handleEcdn11(
  mode: DeliveryMode,
  config: ContentConfig
): Promise<Result> {
  const type = DetectionType.ECDN11;
  try {
    if (config && config.agent && config.agent.disabled) {
      return Promise.reject({ message: DISABLED });
    }
    return get11Data({
      base: { timeout: 2000, doThrow: true },
      config,
      // Not used, but here for completeness of api
      vals: { start: performance.now(), durs: {} }
    }).then(elevenData => {
      if (!elevenData || !deliveryCapable(elevenData.version)) {
        const message = !elevenData ? MISSING_DATA : NOT_CAPABLE;
        return Promise.reject({ message });
      } else {
        dispatchPrimingEvent({ type, success: true });
        // TODO parse version
        return Promise.resolve({ mode, elevenData });
      }
    });
  } catch (e) {
    const error = e as { message: PrimingError };
    const reason = (error && error.message) || UNKNOWN;
    dispatchPrimingEvent({ reason, type, success: false });
    return Promise.reject(error);
  }
}

export default function checkDeliveryMode(
  mode: DeliveryMode,
  config: ContentConfig,
  data: PrimingData
): Promise<Result> {
  log.info('checkDeliveryMode:start', 'checking which mode to use', {
    mode,
    data,
    config
  });
  handleEcdn10DetectionData(data && data.ecdn10);
  switch (mode) {
    // Immediately use WebRTC
    case WEBRTC: {
      const doesSupportWebRTC = supportsWebRTC();
      const type = DetectionType.WEBRTC;
      if (doesSupportWebRTC) {
        dispatchPrimingEvent({ type, success: true });
        return Promise.resolve({ mode });
      } else {
        const reason = PrimingError.UNSUPPORTED;
        dispatchPrimingEvent({ type, success: false, reason });
        return Promise.reject();
      }
    }

    // Immediately use ECDN 11 if detected
    case ECDN11:
      return handleEcdn11(mode, config);

    case ECDN10:
      return handleEcdn10(data, config, mode);

    // Indicate that Kollective delivery cannot be used
    default: {
      const reason = NO_PLAYBACK_METHOD;
      const type = NO_PLAYBACK;
      dispatchPrimingEvent({ type, reason, success: false });
      return Promise.reject({ mode: null });
    }
  }
}
